<!--
  Compact one-row rule editor. Everything flex-wraps in `.rule-editor__body`
  so simple rules collapse to ~48px and complex rules (revenue + timeframe)
  flow onto a second line gracefully without a dashed separator.

  Rule types that need no inputs at all (is_identified, is_anonymous) render
  just the badge + type select + remove — no hint text, no empty input row.
-->
<div class="rule-editor" [formGroup]="form" [attr.data-type]="currentType">
  <!-- Rule-type badge -->
  <div class="rule-editor__badge" [attr.data-type]="currentType">
    <mat-icon>{{ ruleTypeIconMap[currentType] || "rule" }}</mat-icon>
  </div>

  <!-- Body: rule-type select + every type-specific input, all in one flex
       row that wraps as needed. -->
  <div class="rule-editor__body">
    <!-- Rule type chip — opens the flyout's shared "Add criterion" picker to
         change this rule's type (handled as an in-place replace by the parent). -->
    <button
      type="button"
      class="rule-editor__type-btn"
      [matMenuTriggerFor]="sharedTypeMenu"
      (click)="onTypeBtnClick()"
      (menuOpened)="typeMenuOpened.emit()"
      [disabled]="readOnly"
      data-test="rule-editor-type">
      <span class="rule-editor__type-btn-label">{{ ruleTypeLabelMap[currentType] | translate }}</span>
      <mat-icon class="rule-editor__type-btn-arrow">arrow_drop_down</mat-icon>
    </button>

    <!-- Consent: optional version date dropdown. -->
    @if (currentType === "has_consent" || currentType === "has_not_consent") {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
        <mat-label>{{ "data-platform.version-date" | translate }}</mat-label>
        <mat-select formControlName="versionDate">
          <mat-option value="">{{ "data-platform.version-date-any" | translate }}</mat-option>
          @for (v of consentOptions; track v) {
            <mat-option [value]="v">{{ v }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    <!-- event_count: event name autocomplete. -->
    @if (currentType === "event_count") {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
        <mat-label>{{ "data-platform.event-name" | translate }}</mat-label>
        <input
          matInput
          formControlName="eventName"
          [matAutocomplete]="eventNameAuto"
          [placeholder]="optionsLoading ? ('data-platform.loading-options' | translate) : ''"
          data-test="rule-editor-event-name" />
        <mat-autocomplete #eventNameAuto="matAutocomplete">
          @for (opt of eventTypes; track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    }

    @if (isTagRule(currentType)) {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
        <mat-select formControlName="tagMode" [aria-label]="'data-platform.tags' | translate" data-test="rule-editor-tag-mode">
          <mat-option value="any">{{ "data-platform.tag-mode-any" | translate }}</mat-option>
          <mat-option value="none">{{ "data-platform.tag-mode-none" | translate }}</mat-option>
        </mat-select>
      </mat-form-field>
    }

    @if (currentType === "goal_count") {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
        <mat-label>{{ "data-platform.goal-names" | translate }}</mat-label>
        <mat-select
          formControlName="names"
          multiple
          [placeholder]="optionsLoading ? ('data-platform.loading-options' | translate) : ''"
          data-test="rule-editor-goal-names">
          @for (goal of goalOptions; track goal.value) {
            <mat-option [value]="goal.value">{{ goal.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    <!-- Free text: the tag list is not exhaustive. -->
    @if (isTagRule(currentType)) {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field rule-editor__field--list">
        <mat-label>{{ "data-platform.tags" | translate }}</mat-label>
        <mat-chip-grid #listGrid data-test="rule-editor-tags">
          @for (value of currentList; track value) {
            <mat-chip-row (removed)="removeListValue(value)" [attr.data-test]="'rule-editor-chip-' + value">
              {{ value }}
              <button matChipRemove type="button" [attr.aria-label]="'audience.remove-filter' | translate">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip-row>
          }
          <input
            #listTrigger="matAutocompleteTrigger"
            [formControl]="listInput"
            [matChipInputFor]="listGrid"
            [matChipInputSeparatorKeyCodes]="listSeparatorKeys"
            (matChipInputTokenEnd)="addListValue($event)"
            [matAutocomplete]="listAuto"
            [placeholder]="tagPlaceholderKey ? (tagPlaceholderKey | translate) : ''"
            data-test="rule-editor-list-input" />
        </mat-chip-grid>
        <mat-autocomplete #listAuto="matAutocomplete" (optionSelected)="selectListValue($event)">
          @for (opt of listValueOptions; track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    }

    <!-- widget_event_count: widget + event-type selects. -->
    @if (currentType === "widget_event_count") {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
        <mat-label>{{ "data-platform.widget" | translate }}</mat-label>
        <mat-select formControlName="widgetId" data-test="rule-editor-widget">
          @for (w of widgetList; track w.id) {
            <mat-option [value]="w.id">{{ w.campaignName ? w.campaignName + " (" + (w.name || w.id) + ")" : w.name || w.id }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
        <mat-label>{{ "data-platform.widget-event" | translate }}</mat-label>
        <mat-select formControlName="widgetEventType" data-test="rule-editor-widget-event">
          @for (et of widgetEventTypes; track et) {
            <mat-option [value]="et">{{ widgetEventLabelKeys[et] || et | translate }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    <!-- Content interest. Both dimension rule types render here: an affinity
         rule is the same criterion, already carrying a share. -->
    @if (isDimensionRule(currentType)) {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field rule-editor__field--half">
        <mat-label>{{ "data-platform.dimension-type" | translate }}</mat-label>
        <mat-select formControlName="dimensionType" data-test="rule-editor-dimension-type">
          @for (t of availableDimensionTypes; track t) {
            <mat-option [value]="t">{{ dimensionTypeLabels[t] || t }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field rule-editor__field--half">
        <mat-label>{{ "data-platform.dimension-value" | translate }}</mat-label>
        <input
          matInput
          formControlName="dimensionValue"
          [matAutocomplete]="dimAuto"
          [placeholder]="dimensionValuePlaceholderKey ? (dimensionValuePlaceholderKey | translate) : ''"
          data-test="rule-editor-dimension-value" />
        <mat-autocomplete #dimAuto="matAutocomplete" panelClass="rule-editor__value-panel">
          @for (v of dimensionValueOptions; track v) {
            <mat-option [value]="v">{{ v }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>

      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__strength">
        <mat-label>{{ "data-platform.strength" | translate }}</mat-label>
        <mat-select formControlName="strength" panelClass="rule-editor__strength-panel" data-test="rule-editor-strength">
          <!-- Options are two-line; the trigger shows the label alone. -->
          <mat-select-trigger>{{ selectedStrengthLabelKey | translate }}</mat-select-trigger>
          @for (preset of strengthPresets; track preset.strength) {
            <mat-option [value]="preset.strength">
              <span class="strength-option">
                <span class="strength-option__label">{{ preset.labelKey | translate }}</span>
                <span class="strength-option__desc">{{ preset.descKey | translate }}</span>
              </span>
            </mat-option>
          }
          <mat-option value="custom">
            <span class="strength-option">
              <span class="strength-option__label">{{ customStrengthLabelKey | translate }}</span>
              <span class="strength-option__desc">{{ customStrengthDescKey | translate }}</span>
            </span>
          </mat-option>
        </mat-select>
      </mat-form-field>

      @if (form.get("strength")?.value === "custom") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__custom-value">
          <mat-label>{{ "data-platform.strength-share" | translate }}</mat-label>
          <input matInput type="number" formControlName="share" [min]="1" [max]="100" data-test="rule-editor-share" />
          <span matTextSuffix>%</span>
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__custom-value">
          <mat-label>{{ "data-platform.strength-floor" | translate }}</mat-label>
          <input matInput type="number" formControlName="floor" [min]="1" data-test="rule-editor-floor" />
        </mat-form-field>
      }
    }

    <!-- Operator + value for numeric rules. -->
    @if (isOperatorRule(currentType)) {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
        <mat-label>{{ "data-platform.operator" | translate }}</mat-label>
        <mat-select formControlName="operator" (selectionChange)="onOperatorSelected($event.value)" data-test="rule-editor-operator">
          @for (op of rowOperatorOptions; track op.value) {
            <mat-option [value]="op.value">{{ op.labelKey | translate }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      @if (showsValue) {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
          <mat-label>{{ "data-platform.value" | translate }}</mat-label>
          <input
            matInput
            type="number"
            formControlName="value"
            [attr.min]="isCountRule(currentType) ? 1 : 0"
            [attr.step]="isCountRule(currentType) || currentType === 'revenue' || currentType === 'engagement_score' ? 1 : null"
            [attr.max]="currentType === 'engagement_score' ? 100 : null"
            data-test="rule-editor-value" />
          @if (currentType === "revenue" && currencySymbol) {
            <span matTextSuffix>{{ currencySymbol }}</span>
          }
        </mat-form-field>
      }

      @if ((currentType === "revenue" || currentType === "engagement_score") && form.get("operator")?.value === "between") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
          <mat-label>{{ "data-platform.upper-value" | translate }}</mat-label>
          <input
            matInput
            type="number"
            formControlName="upperValue"
            [attr.min]="0"
            [attr.max]="currentType === 'engagement_score' ? 100 : null"
            data-test="rule-editor-upper-value" />
          @if (currentType === "revenue" && currencySymbol) {
            <span matTextSuffix>{{ currencySymbol }}</span>
          }
        </mat-form-field>
      }
    }

    <!-- Revenue: coupon-only toggle. -->
    @if (currentType === "revenue") {
      <mat-slide-toggle formControlName="couponOnly" color="primary" class="rule-editor__toggle">
        {{ "data-platform.coupon-only" | translate }}
      </mat-slide-toggle>
    }

    @if (currentType === "last_active" || currentType === "first_seen") {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
        <mat-label>{{ "data-platform.timeframe" | translate }}</mat-label>
        <mat-select [value]="daysAgoMode" (selectionChange)="onDaysAgoModeChanged($event.value)" data-test="rule-editor-days-ago-mode">
          @for (op of rowOperatorOptions; track op.value) {
            <mat-option [value]="op.value">{{ op.labelKey | translate }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      @if (daysAgoMode === "between") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
          <mat-label>{{ "data-platform.tf-days-ago-start" | translate }}</mat-label>
          <input matInput type="number" formControlName="daysAgo" [min]="1" data-test="rule-editor-days-ago" />
          <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
          <mat-label>{{ "data-platform.tf-days-ago-end" | translate }}</mat-label>
          <input matInput type="number" formControlName="daysAgoUpper" [min]="(form.get('daysAgo')?.value || 0) + 1" data-test="rule-editor-days-ago-upper" />
          <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
        </mat-form-field>
      } @else {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
          <mat-label>{{ "data-platform.days-ago" | translate }}</mat-label>
          <input matInput type="number" formControlName="daysAgo" [min]="1" data-test="rule-editor-days-ago" />
          <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
        </mat-form-field>
      }
    }

    <!-- Timeframe controls — only the revenue & behaviour-with-timeframe
         types need them. Flow inline (no separator), wrap as needed. -->
    @if (
      currentType === "revenue" ||
      currentType === "event_count" ||
      currentType === "goal_count" ||
      currentType === "widget_event_count" ||
      currentType === "total_events" ||
      currentType === "session_count" ||
      currentType === "purchase_count"
    ) {
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
        <mat-label>{{ "data-platform.timeframe" | translate }}</mat-label>
        <mat-select [value]="form.get('timeframeMode')?.value" (selectionChange)="onTimeframeModeChanged($event.value)" data-test="rule-editor-timeframe-mode">
          @for (tf of timeframeModeOptions; track tf.value) {
            <mat-option [value]="tf.value">{{ tf.labelKey | translate }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      @if (form.get("timeframeMode")?.value === "TimeWindow") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
          <mat-label>{{ "data-platform.days-ago" | translate }}</mat-label>
          <input matInput type="number" formControlName="daysAgo" [min]="1" />
          <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
        </mat-form-field>
      }

      @if (form.get("timeframeMode")?.value === "SinceDate" || form.get("timeframeMode")?.value === "BetweenDates") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__date">
          <mat-label>{{ "data-platform.tf-start-date" | translate }}</mat-label>
          <input matInput [matDatepicker]="startPicker" formControlName="startDate" />
          <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
          <mat-datepicker #startPicker></mat-datepicker>
        </mat-form-field>
      }

      @if (form.get("timeframeMode")?.value === "BetweenDates") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__date">
          <mat-label>{{ "data-platform.tf-end-date" | translate }}</mat-label>
          <input matInput [matDatepicker]="endPicker" formControlName="endDate" [min]="form.get('startDate')?.value" />
          <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
          <mat-datepicker #endPicker></mat-datepicker>
        </mat-form-field>
      }

      @if (form.get("timeframeMode")?.value === "BetweenDaysAgo") {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
          <mat-label>{{ "data-platform.tf-days-ago-start" | translate }}</mat-label>
          <input matInput type="number" formControlName="daysAgo" [min]="1" />
          <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
          <mat-label>{{ "data-platform.tf-days-ago-end" | translate }}</mat-label>
          <input matInput type="number" formControlName="daysAgoUpper" [min]="form.get('daysAgo')?.value || 1" />
          <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
        </mat-form-field>
      }
    }

    @if (blockingNoteKey) {
      <p class="rule-editor__note rule-editor__note--warn" data-test="rule-editor-blocking-note">
        <mat-icon>error_outline</mat-icon>
        <span>{{ blockingNoteKey | translate }}</span>
      </p>
    }

    @if (currentType === "engagement_score") {
      <p class="rule-editor__note" data-test="rule-editor-engagement-hint">
        {{ "data-platform.engagement-rule-hint" | translate }}
      </p>
    }

    @if (isDimensionRule(currentType)) {
      @if (noDimensionsConfigured) {
        <p class="rule-editor__note rule-editor__note--warn" data-test="rule-editor-dimensions-off">
          <mat-icon>error_outline</mat-icon>
          <span>{{ "data-platform.dimensions-not-configured" | translate }}</span>
        </p>
      } @else if (dimensionWarningKey) {
        <p class="rule-editor__note rule-editor__note--warn" data-test="rule-editor-dimension-warning">
          <mat-icon>error_outline</mat-icon>
          <span>{{ dimensionWarningKey | translate }}</span>
        </p>
      } @else if (dimensionHint) {
        <p class="rule-editor__note" data-test="rule-editor-dimension-hint">
          {{ dimensionHint.key | translate: dimensionHint.params }}
        </p>
      }
    }
  </div>

  <button
    mat-icon-button
    type="button"
    class="rule-editor__remove"
    (click)="onRemove()"
    [attr.aria-label]="'audience.remove-filter' | translate"
    data-test="rule-editor-remove">
    <mat-icon>close</mat-icon>
  </button>
</div>
